import { useState, useEffect } from 'react';
import { Container, Row, Col, Form, FormGroup, Button } from 'reactstrap';
import Helmet from '../components/Helmet/Helmet';
import { useSelector, useDispatch } from 'react-redux';
import { getUserTier, claimCoupon } from '../components/Helpers/CouponHelper';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { auth, db } from '../firebase.config';
import { addDoc, collection, doc, updateDoc } from 'firebase/firestore';
import '../styles/checkout.css';
import fetchShippingPrice from '../components/Helpers/fetchShippingPrice';
import { countries } from '../assets/data/countries';
import { statesByCountry } from '../assets/data/states';
import { toast } from 'react-toastify';
import 'firebase/functions';
import { useNavigate } from 'react-router-dom';
import { cartActions } from '../redux/slices/cartSlice';
import {v4 as uuidv4} from 'uuid'
import createShippingLabel from '../components/Helpers/createShippingLabel';
import updateProductStock from '../components/Helpers/updateProductStock';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import createIntlShippingLabel from '../components/Helpers/createIntlShippingLabel';


// Load Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPEPUBLISHABLEKEY);

const CheckoutForm = ({ shippingCharges, setShippingCharges, shippingAddress, setShippingAddress, total, oldPoints, setTotal }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [email, setEmail] = useState('');
    const [billingAddress, setBillingAddress] = useState({
        fullName: '',
        phone: '',
        street: '',
        city: '',
        state: '',
        zip: '',
        country: 'United States'
    });
    const [isSameAddress, setIsSameAddress] = useState(false);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [purchaseLoading, setPurchaseLoading] = useState(false);
    const filteredStates = statesByCountry[shippingAddress.country || 'US'] || [];

    const [isAddressValid, setIsAddressValid] = useState(false);
    const [cardComplete, setCardComplete] = useState(false);
    const [zipPlus4, setzipPlus4] = useState("");

    const cartSubTotal = useSelector(state => state.cart.totalAmount);
    const cartItems = useSelector(state => state.cart.cartItems);
    const directPurchaseItem = useSelector(state => state.cart.directPurchaseItem);

    

    const handleShippingChange = (e) => {
        const { name, value } = e.target;
        
        if (name === 'country') {
            // Find the selected country object from the countries array
            const selectedCountry = countries.find((c) => c.code === value);

            setShippingAddress((prev) => ({
                ...prev,
                country: selectedCountry.code,
                countryISOAlpha2Code: selectedCountry.name
            }));
        } else {
            setShippingAddress((prev) => ({
                ...prev,
                [name]: value
            }));
        }

        setIsAddressValid(false);
        setShippingCharges(0);
    };

    const handleBillingChange = (e) => {
        const { name, value } = e.target;
        setBillingAddress((prev) => ({
            ...prev,
            [name]: value
        }));
    };
    const handleCheckboxChange = (e) => {
        setIsSameAddress(e.target.checked);
        if (e.target.checked) {
            setBillingAddress(shippingAddress);
        }
    };
    const handleCardChange = (event) => {
        setCardComplete(event.complete); // event.complete is true when all card fields are filled
    };

    const handleFetchShippingPrice = async() => {
        setLoading(true);
        setShippingAddress(prev => ({ ...prev, phone: shippingAddress.phone.startsWith('+') ? shippingAddress.phone : `+${shippingAddress.phone}` }));
        const response = await fetchShippingPrice(email, shippingAddress, cartItems);
        setzipPlus4(response?.zipPlus4);
        setShippingCharges((Number(response?.shippingPrice) || 0).toFixed(2))
        setLoading(false);
        return;
    };

    const subTotal = directPurchaseItem
        ? directPurchaseItem.price * directPurchaseItem.quantity
        : cartSubTotal;
    const computedTotal = total > 0 ? Number(total) : Math.round((subTotal + Number(shippingCharges)) * 100) / 100;

    const stripe = useStripe();
    const elements = useElements();

    const getCurrentUserId = () => {
        const user = auth.currentUser;
        return user ? user.uid : "0";
    };
      
    const handleSubmit = async (event) => {
        setPurchaseLoading(true);

        setShippingAddress(prev => ({ ...prev, phone: shippingAddress.phone.startsWith('+') ? shippingAddress.phone : `+${shippingAddress.phone}` }));

        // if (!zipPlus4 || zipPlus4.length <= 0) {
        //     toast.error("Invalid Address. Please check your address.");
        //     setPurchaseLoading(false);
        //     return;
        // }
        event.preventDefault();

        if (!stripe || !elements) {
            setPurchaseLoading(false)
            return;
        }

        const cardElement = elements.getElement(CardElement);

        if (!cardComplete) {
            toast.error("Please complete your card details before proceeding.");
            setPurchaseLoading(false)
            return;
        }

        if (!subTotal || subTotal <= 0) {
            toast.error("Add a product to cart before payment")
            setPurchaseLoading(false)
            return;
        }

        if (!shippingCharges || shippingCharges <= 0) {
            toast.error("Enter a shipping address to continue")
            setPurchaseLoading(false)
            return;
        }

        try {
            const response = await fetch(process.env.REACT_APP_PAYMENT_INTENT_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    amount: Math.round(Number(computedTotal) * 100), // amount in cents
                    currency: 'usd',
                    checkoutID: uuidv4(),
                }),
            });

            const data = await response.json();
            const clientSecret = data.clientSecret;
            
            const result = await stripe.confirmCardPayment(clientSecret, {
                payment_method: {
                    card: cardElement,
                    billing_details: {
                        name: billingAddress.fullName,
                        email: email,
                        address: {
                            line1: billingAddress.street,
                            city: billingAddress.street,
                            state: billingAddress.state,
                            postal_code: billingAddress.zip,
                            country: billingAddress.country,
                        },
                    },
                },
            });

            const userId = getCurrentUserId();
            const transactionsRef = await collection(db, "transactions");
            
            // ✅ Log the transaction attempt first (whether it succeeds or fails)
            const transactionDoc = await addDoc(transactionsRef, {
                userId: userId,
                email: email,
                amount: computedTotal,
                paymentMethod: "Stripe",
                billingAddress: billingAddress,
                status: result?.error
                    ? `FAILED, ${error}`
                    : (result?.paymentIntent?.status || "UNKNOWN").toUpperCase(),
                createdAt: new Date().toISOString(),
            });         

            if (result.error) {
                toast.error("Payment failed. Please try again.");
                setPurchaseLoading(false);
                return;
            } if (result.paymentIntent.status === 'succeeded') {

                toast.success('Payment Successful!');

                let labelDetails;

                if (shippingAddress.country === 'US') {
                    labelDetails = await createShippingLabel(zipPlus4, cartItems, shippingAddress);
                } else {
                    labelDetails = await createIntlShippingLabel(cartItems, shippingAddress);
                }

                

                const cleanedCartItems = cartItems.map(item => {
                    const cleaned = {};
                    for (const key in item) {
                        if (item[key] !== undefined) {
                            cleaned[key] = item[key];
                        }
                    }
                    return cleaned;
                });

                // Store additional information in Firestore
                const purchasesRef = collection(db, "purchases");
                const purchaseDoc = await addDoc(purchasesRef, {
                    userId: userId,
                    transactionId: transactionDoc.id,
                    orderDetails: cleanedCartItems,
                    itemsPrice: subTotal,
                    shippingFee: shippingCharges,
                    amountPaid: computedTotal,
                    createdAt: new Date().toISOString(),
                });

                console.log("Purchase Document ID:", purchaseDoc);

                await updateProductStock(cleanedCartItems);

                // ✅ Store shipping details only if payment was successful
                const shippingRef = collection(db, "shipping");
                await addDoc(shippingRef, {
                    purchaseId: purchaseDoc.id,
                    userId: userId,
                    shippingAddress: shippingAddress,
                    shippingFee: shippingCharges,
                    labelImage: labelDetails?.labelImage || null,
                    status: "PENDING", // ✅ pending, shipped, delivered
                    createdAt: new Date().toISOString(),
                    updatedAt: null
                });
                console.log("Shipping Document added successfully", shippingRef);

                if (userId !== "0") {
                    const userDocRef = doc(db, "users", userId);
                    await updateDoc(userDocRef, {
                        points: Number(oldPoints) + Number(Math.floor(computedTotal)) || 0,
                    });
                } 
                console.log("User points updated successfully");
              
                setPurchaseLoading(false)

                dispatch(cartActions.clearCart());

                navigate("/thank-you", {
                    state: {
                        purchaseId: purchaseDoc.id,
                        orderDetails: cleanedCartItems,
                        amountPaid: computedTotal,
                        oldPoints: oldPoints,
                        pointsEarned: userId !== 0 ? Math.floor(computedTotal) : 0,
                        shippingURL: labelDetails?.links?.find(link => link.href.includes('TrackConfirmAction'))?.href || null
                    },
                });
            }
        } catch (error) {
            toast.error("Transaction failed. Unable to complete", error)
            console.log(error)
            setPurchaseLoading(false)
        }
    };
      
    return (
        <div className="relative">
            {purchaseLoading &&
                <div className="video-loader">
                    <div className="spinner"></div>
                </div>
            }

            <Form onSubmit={handleSubmit}>
                <div className='form-section'>
                    <span className='form-subtitle'>
                        <h6>Customer Info</h6>
                        <p>* Required</p>
                    </span>
                    <FormGroup className='form-group'>
                        <label>Email *</label>
                        <input
                            type='email'
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                        />
                    </FormGroup>
                </div>

                <div className='form-section'>
                    <span className='form-subtitle'>
                        <h6>Shipping Address</h6>
                        <p>* Required</p>
                    </span>
                    <FormGroup className='form-group'>
                        <label>Full Name *</label>
                        <input
                            name='fullName'
                            type='text'
                            value={shippingAddress.fullName}
                            onChange={handleShippingChange}
                            required
                        />
                    </FormGroup>
                    <FormGroup className='form-group'>
                        <label>Phone Number *</label>
                        <PhoneInput
                            country={shippingAddress.country.toLowerCase()} // Default country
                            preferredCountries={['us', 'gb', 'ca']}
                            inputProps={{
                                name: 'phone',
                                required: true,
                            }}
                            enableSearch={true}
                            autoFormat={true}
                            prefix='+'
                            value={shippingAddress.phone}
                            countryCodeEditable={false}
                            autocompleteSearch={true}
                            onChange={(phone) =>
                                setShippingAddress((prev) => ({ ...prev, phone }))
                            }
                            inputStyle={{
                                backgroundColor: '#f5f5f5',
                                border: '1px solid #e6e6e6',
                                padding: '.5rem 3rem',
                                width: '100%',
                                marginTop: '.2rem',
                                fontSize: '.9rem',
                            }}
                            dropdownStyle={{
                                fontSize: '.9rem',
                                maxHeight: '200px',
                                overflowY: 'auto',
                                marginTop: '0',
                                padding: '0',
                            }}
                            renderStringAsFlag=''
                            placeholder=''
                            disableSearchIcon
                        />
                    </FormGroup>
                    <FormGroup className='form-group'>
                        <label>Street Address *</label>
                        <input
                            name='street'
                            type='text'
                            value={shippingAddress.street}
                            onChange={handleShippingChange}
                            required
                        />
                    </FormGroup>
                    <Row>
                        <Col md={4}>
                            <FormGroup className='form-group'>
                                <label>City *</label>
                                <input
                                    name='city'
                                    type='text'
                                    value={shippingAddress.city}
                                    onChange={handleShippingChange}
                                    required
                                />
                            </FormGroup>
                        </Col>
                        <Col md={4}>
                            <FormGroup className='form-group'>
                                <label>State/Province *</label>
                                <select
                                    name='state'
                                    value={shippingAddress.state}
                                    onChange={handleShippingChange}
                                    required
                                >
                                    <option value="">Select a State</option>
                                    {filteredStates.map((state) => (
                                        <option key={state.code} value={state.code}>
                                        {state.name}
                                        </option>
                                    ))}
                                </select>
                            </FormGroup>
                        </Col>
                        <Col md={4}>
                            <FormGroup className='form-group'>
                                <label>Zip/Postal Code *</label>
                                <input
                                    name='zip'
                                    type='text'
                                    value={shippingAddress.zip}
                                    onChange={handleShippingChange}
                                    required
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    <FormGroup className='form-group'>
                        <label>Country *</label>
                        <select
                            name='country'
                            value={shippingAddress.country}
                            onChange={handleShippingChange}
                            required
                        >
                            <option value="">-- Select a Country --</option>
                            {countries.map((country) => (
                                <option key={country.code} value={country.code}>
                                    {country.name}
                                </option>
                            ))}
                        </select>
                    </FormGroup>

                    <Button className="btn-form" type="button" onClick={handleFetchShippingPrice} disabled={loading || isAddressValid}>
                        {loading ? 'Calculating...' : 'Add Shipping Information'}
                    </Button>
                </div>

                <div className='form-section'>
                    <span className='form-subtitle'>
                        <h6>Shipping Method</h6>
                    </span>
                    <FormGroup className='form-group'>
                        <div className='radio-input'>
                            <input
                                type='radio'
                                role='button'
                                checked
                                readOnly
                            />
                            <label>Shipping Charges</label>
                        </div>
                    </FormGroup>
                </div>
                
                {shippingCharges > 0 && (
                    <>
                        <div className='form-section'>
                            <span className='form-subtitle'>
                                <h6>Payment Info</h6>
                                <p>* Required</p>
                            </span>
                            <FormGroup className='form-group'>
                                <label>Card Number *</label>
                                <CardElement onChange={handleCardChange}  />
                            </FormGroup>
                            <FormGroup className='form-group'>
                                <div className='radio-input'>
                                    <input
                                        type='checkbox'
                                        role='button'
                                        checked={isSameAddress}
                                        onChange={handleCheckboxChange}
                                    />
                                    <label>Billing address same as shipping</label>
                                </div>
                            </FormGroup>
                        </div>

                        {!isSameAddress &&
                            <div className='form-section billing-address'>
                                <span className='form-subtitle'>
                                    <h6>Billing Address</h6>
                                    <p>* Required</p>
                                </span>
                                <FormGroup className='form-group'>
                                    <label>Full Name *</label>
                                    <input
                                        name='fullName'
                                        type='text'
                                        value={billingAddress.fullName}
                                        onChange={handleBillingChange}
                                        required
                                    />
                                </FormGroup>
                                <FormGroup className='form-group'>
                                    <label>Street Address *</label>
                                    <input
                                        name='street'
                                        type='text'
                                        value={billingAddress.street}
                                        onChange={handleBillingChange}
                                        required
                                    />
                                </FormGroup>
                                <Row>
                                    <Col md={4}>
                                        <FormGroup className='form-group'>
                                            <label>City *</label>
                                            <input
                                                name='city'
                                                type='text'
                                                value={billingAddress.city}
                                                onChange={handleBillingChange}
                                                required
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md={4}>
                                        <FormGroup className='form-group'>
                                            <label>State/Province *</label>
                                            <select
                                                name='state'
                                                value={billingAddress.state}
                                                onChange={handleBillingChange}
                                                required
                                            >
                                                <option value="">-- Select a State --</option>
                                                {filteredStates.map((state) => (
                                                    <option key={state.code} value={state.code}>
                                                    {state.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </FormGroup>
                                    </Col>
                                    <Col md={4}>
                                        <FormGroup className='form-group'>
                                            <label>Zip/Postal Code *</label>
                                            <input
                                                name='zip'
                                                type='text'
                                                value={billingAddress.zip}
                                                onChange={handleBillingChange}
                                                required
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <FormGroup className='form-group'>
                                    <label>Country *</label>
                                    <input
                                        name='country'
                                        type='text'
                                        value={billingAddress.country}
                                        readOnly
                                    />
                                </FormGroup>
                            </div>
                        }

                        <Button type="submit" disabled={purchaseLoading}>
                            {purchaseLoading ? 'Processing...' : 'Place Order'}
                        </Button>
                        {error && <div>{error}</div>}
                    </>
                )}
            </Form>
        </div>
    );
};

const Checkout = () => {
    const [shippingCharges, setShippingCharges] = useState(0);
    const [shippingAddress, setShippingAddress] = useState({
        fullName: '',
        phone: '+',
        street: '',
        city: '',
        state: '',
        zip: '',
        country: 'US',
        countryISOAlpha2Code: 'United States'
    });
    const [useCoupon, setUseCoupon] = useState(false);
    const [appliedDiscount, setAppliedDiscount] = useState(0);
    const [total, setTotal] = useState(0);
    const [oldPoints, setOldPoints] = useState(0);
    const dispatch = useDispatch();
    const cartSubTotal = useSelector(state => state.cart.totalAmount);
    const cartItems = useSelector(state => state.cart.cartItems);
    const directPurchaseItem = useSelector(state => state.cart.directPurchaseItem);
    const subTotal = directPurchaseItem
        ? directPurchaseItem.price * directPurchaseItem.quantity
        : cartSubTotal;

    const user = useSelector(state => state.user.user);
    // const total = Math.round((subTotal + Number(shippingCharges)) * 100) / 100;
    const userTier = user ? getUserTier(user.points, user.claimedCoupons) : null;
    const discountPercentage = userTier ? userTier.discount : 0;
    const discountAmount = (subTotal * discountPercentage) / 100;

    useEffect(() => {
        if (user) {
            setOldPoints(user.points || 0);
        }
    }, [user]);

    useEffect(() => {
        if (useCoupon) {
            dispatch(claimCoupon());
        }
    }, [useCoupon, dispatch]);

    const applyCoupon = () => {
        if (userTier) {
            const discountAmount = (subTotal * userTier.discount) / 100;
            toast.success(`Coupon applied! You saved ${discountAmount.toFixed(2)} USD.`);
            setAppliedDiscount(discountAmount);
            setUseCoupon(true);
        }
    };

    useEffect(() => {
        const computedTotal = Math.round((subTotal + Number(shippingCharges) - appliedDiscount) * 100) / 100;
        setTotal(computedTotal);
    }, [subTotal, shippingCharges, appliedDiscount]);

    return (
        <Helmet title="Checkout">
            <section className='bg-color checkout'>
                <Container>
                    <Row>
                        <Col md={6} sm={12} className='form-grid'>
                            <div className='form-col float-end'>
                                <Elements stripe={stripePromise}>
                                    <CheckoutForm 
                                        setShippingCharges={setShippingCharges}
                                        shippingCharges={shippingCharges}
                                        shippingAddress={shippingAddress}
                                        setShippingAddress={setShippingAddress}
                                        total={total}
                                        setTotal={setTotal}
                                        oldPoints={oldPoints}
                                    />
                                </Elements>
                            </div>
                        </Col>
                        <Col md={3} sm={12} className='order-summary'>
                            <div className='form-section'>
                                <span className='form-subtitle'>
                                    <h6>Order Summary</h6>
                                </span>
                                <span className='fee-details'>
                                    <span className='price'>
                                        <p>Subtotal</p>
                                        <p>{subTotal.toFixed(2)} USD</p>
                                    </span>
                                    <span className='price'>
                                        <p>Shipping Charges</p>
                                        <p>{(Number(shippingCharges) || 0).toFixed(2)} USD</p>
                                    </span>
                                    <span className='price'>
                                        <p>Total</p>
                                        <p>{Number(total.toFixed(2))} USD</p>
                                    </span>
                                </span>
                            </div>

                           {user && (
                                <div>
                                    <div style={{ padding: '1rem', backgroundColor: 'white', marginBottom: '1rem' }}>
                                        Available Points: {user.points}
                                    </div>

                                    {userTier && !useCoupon && (
                                        <div className="coupon-section">
                                            <p>🎉 You are eligible for a {userTier.discount}% discount with coupon: <strong>{userTier.coupon}</strong></p>
                                            <button onClick={applyCoupon}>Apply Coupon</button>
                                        </div>
                                    )}

                                    {useCoupon && (
                                        <div className="coupon-section">
                                            <p>✅ Coupon Applied! You saved {appliedDiscount.toFixed(2)} USD.</p>
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className='checkout_cart-items-container'>
                                {directPurchaseItem ? (
                                    <div className='checkout_product-wrapper' key={directPurchaseItem.itemId}>
                                        <div className='checkout_cart-desc'>
                                            <img
                                                src={directPurchaseItem.imgUrl}
                                                loading='lazy'
                                                alt={directPurchaseItem.productName}
                                                className='checkout_item-image'
                                            />
                                            <div className='checkout_cart-details'>
                                                <p className='p-name'>{directPurchaseItem.productName}</p>
                                                <p>{directPurchaseItem.price} USD</p>
                                                {directPurchaseItem.size && <p>SIZE: {directPurchaseItem.size}</p>}
                                            </div>
                                        </div>
                                        <div className='checkout_input'>
                                            <span>Qty {directPurchaseItem.quantity}</span>
                                        </div>
                                    </div>
                                ) : (
                                    cartItems.map((item) => (
                                        <div className='checkout_product-wrapper' key={`${item.itemId}-${item.size || ''}`}>
                                            <div className='checkout_cart-desc'>
                                                <img
                                                    src={item.imgUrl}
                                                    loading='lazy'
                                                    alt={item.productName}
                                                    className='checkout_item-image'
                                                />
                                                <div className='checkout_cart-details'>
                                                    <p className='p-name'>{item.productName}</p>
                                                    <p>{item.price} USD</p>
                                                    {item.size && <p>SIZE: {item.size}</p>}
                                                </div>
                                            </div>
                                            <div className='checkout_input'>
                                                <span>Qty {item.quantity}</span>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>
        </Helmet>
    );
};

export default Checkout;
